import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Globe, Loader2, ExternalLink, Copy, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { ActionButton, SectionHeader } from './VisualAtoms';
import {
  getArtistWebsite,
  publishArtistWebsite,
  updateArtistPage,
} from '../lib/artworksApi';

/**
 * Curator block for publishing an artist's public website page
 * (`/artists/{slug}`). Idempotent: pressing "Publish" the first time
 * creates the page + slug; pressing it again re-publishes an
 * unpublished/hidden page.
 */
export default function ArtistWebsiteBlock({ artistId }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    getArtistWebsite(artistId)
      .then((r) => setPage(r.page))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [artistId]);

  const publicUrl = page?.slug ? `${window.location.origin}/artists/${page.slug}` : null;
  const isPublished = page?.status === 'published';

  const doPublish = async () => {
    setBusy(true);
    try {
      const p = await publishArtistWebsite(artistId);
      setPage(p);
      toast.success(isPublished ? 'Artist website re-published' : 'Artist website published');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to publish artist website');
    } finally {
      setBusy(false);
    }
  };

  const doHide = async () => {
    if (!page?.id) return;
    setBusy(true);
    try {
      const updated = await updateArtistPage(page.id, { status: 'hidden' });
      setPage(updated);
      toast.success('Artist website hidden');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="border border-[#1a1a1a] bg-[#0a0a0a] p-5"
      data-testid="artist-website-block"
    >
      <SectionHeader
        title="Public artist website"
        icon={Globe}
        helper="Create a public /artists/{slug} page that shows this artist's bio + published artworks."
      />

      {loading ? (
        <div className="flex items-center gap-2 text-[11px] font-mono text-[#666]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Status line */}
          <div className="mb-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-1">Status</div>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.14em]"
                style={isPublished
                  ? { color: '#7ed4a0', borderColor: 'rgba(126,212,160,0.4)', background: 'rgba(126,212,160,0.08)' }
                  : page
                    ? { color: '#e4c07a', borderColor: 'rgba(228,192,122,0.4)', background: 'rgba(228,192,122,0.08)' }
                    : { color: '#666', borderColor: '#222' }
                }
              >
                {page ? page.status : 'not_created'}
              </span>
              {page?.published_at && (
                <span className="text-[10px] font-mono text-[#666]">
                  since {new Date(page.published_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {publicUrl && (
            <div className="mb-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-1">Public URL</div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="artist-website-url"
                  readOnly
                  value={publicUrl}
                  className="flex-1 bg-[#050505] border border-[#1a1a1a] px-3 py-2 text-[12px] font-mono text-[#90E0EF] focus:outline-none"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('URL copied'); }}
                  className="p-2 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[#888] hover:text-[#90E0EF] transition-colors"
                  title="Copy URL"
                ><Copy className="w-3.5 h-3.5" /></button>
                {isPublished && (
                  <a
                    href={publicUrl}
                    target="_blank" rel="noreferrer"
                    className="p-2 border border-[#1a1a1a] hover:border-[#90E0EF]/40 text-[#888] hover:text-[#90E0EF] transition-colors"
                    title="Open public page"
                  ><ExternalLink className="w-3.5 h-3.5" /></a>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <ActionButton
              data-testid="artist-website-publish-btn"
              variant={isPublished ? 'secondary' : 'cyan'}
              icon={busy ? Loader2 : (isPublished ? RotateCcw : Globe)}
              disabled={busy}
              onClick={doPublish}
            >
              {busy ? 'Working…' : (isPublished ? 'Re-publish (refresh)' : (page ? 'Publish' : 'Publish Artist Website'))}
            </ActionButton>
            {isPublished && (
              <ActionButton
                data-testid="artist-website-hide-btn"
                variant="warning"
                icon={EyeOff}
                disabled={busy}
                onClick={doHide}
              >
                Hide
              </ActionButton>
            )}
          </div>

          <p className="text-[10px] font-mono text-[#555] mt-3 leading-relaxed">
            The page pulls bio + statement from this artist&apos;s profile, and lists every artwork
            currently in <span className="text-[#90E0EF]">Published</span> status.
          </p>
        </>
      )}
    </motion.section>
  );
}
